import {
  BoxGeometry,
  CatmullRomCurve3,
  CylinderGeometry,
  ExtrudeGeometry,
  Path,
  Shape,
  TubeGeometry,
  Vector2,
  Vector3,
} from 'three';
import { mergeParts } from './merge.js';
import { logarithmicSpiral } from './spiral.js';

/**
 * "…where perfect telescopes and violins are made."
 *
 * The violin is built from its own plan drawing: a half-outline in units of
 * body length, mirrored and splined into a Shape, pierced by two f-holes and
 * extruded with a bevel that stands in for the arching of the plates. The
 * scroll is a logarithmic spiral — the same curve as the stair and the shells,
 * which is the whole joke of Isidora.
 *
 * Returned as geometries grouped by material so the layer can decide how
 * varnish, ebony and gut catch the last of the light.
 *
 * Local frame: body length 1.0 along +Y, face toward +Z, origin at body centre.
 */

/**
 * The half-outline of a violin plate, in units of body length, taken from a
 * full-size instrument: 356 mm body, 208 mm lower bout, 168 mm upper bout,
 * 112 mm waist.
 *
 * It is split into three runs rather than one, because the two junctions are
 * the C-bout corners — the sharp points that make a violin read as a violin
 * at a glance. A single spline through all of them rounds the corners off
 * and the instrument turns into a gourd.
 */
const LOWER_BOUT = [
  [0.004, -0.5],
  [0.09, -0.485],
  [0.172, -0.458],
  [0.238, -0.418],
  [0.279, -0.359],
  [0.292, -0.3],
  [0.287, -0.231],
  [0.263, -0.176],
  [0.228, -0.14],
];
const C_BOUT = [
  [0.228, -0.14],
  [0.159, -0.101],
  [0.138, -0.042],
  [0.14, 0.014],
  [0.162, 0.061],
  [0.206, 0.098],
];
const UPPER_BOUT = [
  [0.206, 0.098],
  [0.228, 0.151],
  [0.236, 0.205],
  [0.229, 0.266],
  [0.204, 0.321],
  [0.164, 0.386],
  [0.114, 0.441],
  [0.062, 0.478],
  [0.036, 0.5],
];

const v2 = ([x, y], mirror = 1) => new Vector2(x * mirror, y);

function bodyShape() {
  const shape = new Shape();
  const runs = [LOWER_BOUT, C_BOUT, UPPER_BOUT];

  shape.moveTo(LOWER_BOUT[0][0], LOWER_BOUT[0][1]);
  for (const run of runs) shape.splineThru(run.slice(1).map((p) => v2(p)));
  // Down the other side, in reverse, mirrored.
  for (const run of [...runs].reverse()) {
    const mirrored = [...run].reverse().map((p) => v2(p, -1));
    shape.splineThru(mirrored);
  }
  shape.closePath();
  shape.holes.push(fHole(-1), fHole(1));
  return shape;
}

/**
 * One f-hole, built as a swept slot rather than a bezier outline: a
 * centreline with a width profile, sampled up one side and back down the
 * other. Bezier f-holes self-intersect at the eyes and the triangulator
 * turns them into a single black smear.
 */
function fHole(side) {
  const bottom = -0.2;
  const top = 0.085;
  const centre = side * 0.112;
  const lean = side * 0.03;
  const steps = 26;

  const at = (u) => {
    const y = bottom + (top - bottom) * u;
    // A gentle S: the hole leans out at the bottom and in at the top.
    const x = centre + lean * Math.sin((u - 0.5) * Math.PI);
    // The two eyes, plus the narrow throat between them.
    const eyeLow = 0.03 * Math.exp(-Math.pow((u - 0.03) / 0.11, 2));
    const eyeHigh = 0.024 * Math.exp(-Math.pow((u - 0.97) / 0.1, 2));
    const nick = 0.012 * Math.exp(-Math.pow((u - 0.5) / 0.11, 2));
    // The throat has to survive the bevel, which eats into every contour.
    return { x, y, w: 0.024 + eyeLow + eyeHigh + nick };
  };

  const path = new Path();
  const left = [];
  const right = [];
  for (let i = 0; i <= steps; i++) {
    const s = at(i / steps);
    left.push([s.x - s.w, s.y]);
    right.push([s.x + s.w, s.y]);
  }
  path.moveTo(left[0][0], left[0][1]);
  for (let i = 1; i < left.length; i++) path.lineTo(left[i][0], left[i][1]);
  for (let i = right.length - 1; i >= 0; i--) path.lineTo(right[i][0], right[i][1]);
  path.closePath();
  return path;
}

function bodyGeometry() {
  const depth = 0.1;
  const geo = new ExtrudeGeometry(bodyShape(), {
    depth,
    // Each bout is one spline, so this is the resolution of a bout, not of
    // the whole outline. Too few and the waist flattens out.
    curveSegments: 40,
    // A small bevel stands in for the arching of the plates. It also eats
    // `bevelSize` out of every contour, including the f-holes, which is why
    // it is kept this shallow.
    bevelEnabled: true,
    bevelThickness: 0.022,
    bevelSize: 0.006,
    bevelOffset: 0,
    bevelSegments: 4,
  });
  geo.translate(0, 0, -depth * 0.5);
  return geo;
}

function neckGeometry() {
  const parts = [];

  // Neck: a tapering block running from the body block up to the nut.
  const neck = new BoxGeometry(0.1, 0.44, 0.075);
  neck.translate(0, 0.705, -0.028);
  parts.push(neck);

  // Heel, where the neck is let into the body.
  const heel = new BoxGeometry(0.115, 0.1, 0.11);
  heel.translate(0, 0.52, -0.012);
  parts.push(heel);

  // Pegbox.
  const pegbox = new BoxGeometry(0.084, 0.21, 0.098);
  pegbox.translate(0, 1.0, -0.05);
  parts.push(pegbox);

  // The volute: a logarithmic spiral turned into a tube and stood on its side,
  // so the eye of the scroll faces the player.
  const pts = logarithmicSpiral({ turns: 1.9, growth: 2.75, startRadius: 0.012, segments: 110 });
  const curve = new CatmullRomCurve3(pts.map((p) => new Vector3(p.x, p.y, 0)));
  const volute = new TubeGeometry(curve, 110, 0.03, 10, false);
  volute.rotateY(Math.PI / 2);
  volute.rotateX(Math.PI * 0.08);
  volute.translate(0, 1.125, -0.085);
  parts.push(volute);

  return mergeParts(parts);
}

function ebonyGeometry() {
  const parts = [];

  // Fingerboard, sweeping off the end of the neck over the belly.
  const fb = new BoxGeometry(0.098, 0.6, 0.03);
  fb.translate(0, 0.61, 0.028);
  parts.push(fb);

  // Nut.
  const nut = new BoxGeometry(0.1, 0.018, 0.038);
  nut.translate(0, 0.903, 0.02);
  parts.push(nut);

  // Tailpiece.
  const tail = new BoxGeometry(0.09, 0.19, 0.026);
  tail.translate(0, -0.345, 0.07);
  parts.push(tail);

  // Chinrest, clamped over the lower-left edge where a jaw would rest.
  const chin = new CylinderGeometry(0.05, 0.056, 0.045, 14);
  chin.scale(1.25, 1, 0.6);
  chin.rotateX(Math.PI / 2);
  chin.rotateZ(-0.25);
  chin.translate(-0.135, -0.405, 0.068);
  parts.push(chin);

  // Four pegs through the pegbox.
  for (let i = 0; i < 4; i++) {
    const side = i % 2 === 0 ? 1 : -1;
    const y = 0.955 + Math.floor(i / 2) * 0.085;
    const peg = new CylinderGeometry(0.009, 0.015, 0.1, 10);
    peg.rotateZ(Math.PI / 2);
    peg.translate(side * 0.068, y, -0.05);
    parts.push(peg);
    const head = new CylinderGeometry(0.026, 0.02, 0.022, 10);
    head.rotateZ(Math.PI / 2);
    head.translate(side * 0.128, y, -0.05);
    parts.push(head);
  }

  return mergeParts(parts);
}

function bridgeGeometry() {
  const bridge = new BoxGeometry(0.12, 0.09, 0.012);
  bridge.translate(0, -0.055, 0.082);
  return bridge;
}

function stringsGeometry() {
  const parts = [];
  const xs = [-0.031, -0.0105, 0.0105, 0.031];
  for (const x of xs) {
    const from = new Vector3(x * 0.78, -0.3, 0.083);
    const to = new Vector3(x, 0.903, 0.04);
    const len = from.distanceTo(to);
    const g = new CylinderGeometry(0.0028, 0.0028, len, 5, 1);
    const mid = from.clone().lerp(to, 0.5);
    // Align +Y to the run of the string.
    const dir = to.clone().sub(from).normalize();
    const angle = Math.atan2(dir.z, dir.y);
    g.rotateX(-angle);
    g.translate(mid.x, mid.y, mid.z);
    parts.push(g);
  }
  return mergeParts(parts);
}

/**
 * @returns {{varnish: import('three').BufferGeometry,
 *            ebony: import('three').BufferGeometry,
 *            strings: import('three').BufferGeometry}}
 */
export function createViolinGeometry() {
  const varnish = mergeParts([bodyGeometry(), neckGeometry(), bridgeGeometry()]);
  varnish.computeVertexNormals();
  const ebony = ebonyGeometry();
  const strings = stringsGeometry();
  for (const g of [varnish, ebony, strings]) g.computeBoundingSphere();
  return { varnish, ebony, strings };
}

/**
 * The bow: a stick with a gentle camber and a ribbon of hair. A bow is
 * longer than the violin it plays — 75 cm against 59.5 — so in body-length
 * units it runs past ±1.
 */
export function createBowGeometry() {
  const stick = new CatmullRomCurve3([
    new Vector3(0, -1.0, 0.0),
    new Vector3(0, -0.34, -0.022),
    new Vector3(0, 0.4, -0.026),
    new Vector3(0, 1.04, 0.0),
  ]);
  const wood = new TubeGeometry(stick, 56, 0.0085, 8, false);
  const frog = new BoxGeometry(0.032, 0.085, 0.042);
  frog.translate(0, -0.95, 0.02);
  const hair = new BoxGeometry(0.017, 1.95, 0.004);
  hair.translate(0, 0.02, 0.026);
  return {
    wood: mergeParts([wood, frog]),
    hair,
  };
}
